import { useFormConfigStore } from '../form-config/state/store';
import { isValidConstantName, type ConstantDecl } from '@capture/shared';
import { SectionCard } from './panel/SectionCard';

/**
 * P8 — job-level named constants authoring. Rows of (name, type, value) that
 * rule authors reference in conditions ("A named constant…") and expressions
 * (`{const.<name>}`). Persists through the standard draft autosave path via the
 * store's `setConstants` (the whole map is replaced on each edit). Publish gates
 * on any rule referencing a constant that isn't declared here.
 */
type ConstantType = ConstantDecl['type'];

export function ConstantsSection() {
  // Select the stable node reference (undefined or the map) — defaulting inside
  // the selector would return a fresh `{}` each render and loop the store sub.
  const constants = useFormConfigStore((s) => s.contentV3?.constants) ?? {};
  const setConstants = useFormConfigStore((s) => s.setConstants);

  const rows = Object.entries(constants);

  function commit(next: Record<string, ConstantDecl>) {
    setConstants(next);
  }

  function addRow() {
    // Choose a unique placeholder name so a second add doesn't collide.
    let i = rows.length + 1;
    let name = `constant_${i}`;
    while (constants[name]) name = `constant_${++i}`;
    commit({ ...constants, [name]: { type: 'number', value: '' } });
  }

  function renameRow(oldName: string, newName: string) {
    // Rebuild preserving order; skip when the new name collides with a different row.
    if (newName !== oldName && constants[newName]) return;
    const next: Record<string, ConstantDecl> = {};
    for (const [n, decl] of rows) next[n === oldName ? newName : n] = decl;
    commit(next);
  }

  function setType(name: string, type: ConstantType) {
    commit({ ...constants, [name]: { ...constants[name]!, type } });
  }

  function setValue(name: string, raw: string) {
    const decl = constants[name]!;
    const value: string | number = decl.type === 'number' && raw !== '' && !isNaN(Number(raw)) ? Number(raw) : raw;
    commit({ ...constants, [name]: { ...decl, value } });
  }

  function removeRow(name: string) {
    const next = { ...constants };
    delete next[name];
    commit(next);
  }

  return (
    <div data-testid="constants-section">
      <SectionCard title="Constants" summary={`${rows.length} constant${rows.length === 1 ? '' : 's'}`} defaultOpen>
        <div className="constants-rows">
          {rows.length === 0 && (
            <p className="constants-empty" data-testid="constants-empty">
              No constants yet. Add one to reuse a threshold or value across rules.
            </p>
          )}
          {rows.map(([name, decl]) => {
            const nameInvalid = !isValidConstantName(name);
            return (
              <div className="constants-row" data-testid={`constant-row-${name}`} key={name}>
                <input
                  className="cond-input constant-name"
                  data-testid={`constant-name-${name}`}
                  aria-label="Constant name"
                  value={name}
                  onChange={(e) => renameRow(name, e.target.value)}
                  placeholder="approval_limit"
                  aria-invalid={nameInvalid || undefined}
                />
                <select
                  className="cond-select constant-type"
                  data-testid={`constant-type-${name}`}
                  aria-label="Constant type"
                  value={decl.type}
                  onChange={(e) => setType(name, e.target.value as ConstantType)}
                >
                  <option value="number">number</option>
                  <option value="text">text</option>
                  <option value="date">date</option>
                </select>
                <input
                  className="cond-input constant-value"
                  data-testid={`constant-value-${name}`}
                  aria-label="Constant value"
                  type={decl.type === 'date' ? 'date' : decl.type === 'number' ? 'number' : 'text'}
                  value={String(decl.value ?? '')}
                  onChange={(e) => setValue(name, e.target.value)}
                />
                <button
                  className="constants-remove"
                  data-testid={`constant-remove-${name}`}
                  onClick={() => removeRow(name)}
                  title="Remove constant"
                  aria-label={`Remove constant ${name}`}
                >
                  &times;
                </button>
                {nameInvalid && (
                  <span className="constants-name-error" data-testid={`constant-name-error-${name}`}>
                    Names must start with a letter and use only letters, digits, or underscore.
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <button className="btn constants-add" data-testid="constant-add" onClick={addRow}>
          + Add constant
        </button>
      </SectionCard>
    </div>
  );
}
